import { Box, Button, Stack, Typography, Chip, Snackbar } from "@mui/material";
import { OrderTrackerDrawer } from "@pages/OrderTracker/components";
import useOrderTrackerDrawer from "@pages/OrderTracker/components/OrderTrackerDrawer/useOrderTrackerDrawer";
import {
  useWebApiV1ListSiteGroupsQuery,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { useState } from "react";
import { useParams } from "react-router-dom";
import LeftSideBar from "./components/LeftSideBar";
import PageHeader from "@pages/Layout/PageHeader";
import PhysicianReviewContent from "./components/PhysicianReviewContent";
import ApprovePlanModal from "./components/ApprovePlanModal";
import RejectPlanModal from "./components/RejectPlanModal";
import { MAIN_CONTENT_MAX_WIDTH } from "../../constants";
import DrawerControlButtons from "@pages/OrderTracker/components/DrawerControlButtons";
import { OrderStatus } from "@providers/hop-ord-server/api";
import { orderStatusMapping, OrderStatus as OrderStatusEnum } from "@enums";

const scrollToId = (id: string) => {
  const element = document.getElementById(id);
  element?.scrollIntoView({ behavior: "smooth" });
};

const statusLabel = (orderStatus: OrderStatus) => {
  switch (orderStatus) {
    case "ready_for_ro_approval":
      return "Pending";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    default:
      return orderStatusMapping[orderStatus];
  }
};

const statusColor = (orderStatus: OrderStatus) => {
  switch (orderStatus) {
    case "ready_for_ro_approval":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "error";
    default:
      return "warning";
  }
};

const PhysicianReviewPage = () => {
  const { orderId } = useParams();
  const { data: orderData } = useWebApiV1OrderQuery({
    orderId: Number(orderId),
  });

  const { data: siteGroupData } = useWebApiV1ListSiteGroupsQuery({
    orderId: Number(orderId),
  });

  const [approvePlanModalOpen, setApprovePlanModalOpen] = useState(false);
  const [rejectPlanModalOpen, setRejectPlanModalOpen] = useState(false);

  const status = orderData?.status;

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const {
    drawerOpen,
    setDrawerOpen,
    selectedDrawerPanel,
    setSelectedDrawerPanel,
  } = useOrderTrackerDrawer();

  const handleApprove = () => {
    setApprovePlanModalOpen(false);
    setToastMessage(
      "You've approved the plan. The planning team has been notified.",
    );
    setToastOpen(true);
    scrollToId("plan-review-title");
  };

  const handleReject = (success: boolean) => {
    setRejectPlanModalOpen(false);
    const message = success
      ? "Plan rejected. Your reason has been shared with the planning team."
      : "Error rejecting plan. Please try again or contact support.";
    setToastMessage(message);
    setToastOpen(true);
    scrollToId("plan-review-title");
  };

  return (
    <>
      <Stack direction="row" height={1}>
        <LeftSideBar />
        <Stack sx={{ width: 1, overflow: "hidden" }}>
          <PageHeader orderData={orderData} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 3,
              overflowY: "scroll",
              flexGrow: 1,
            }}
          >
            <Stack
              gap={2}
              sx={{
                maxWidth: MAIN_CONTENT_MAX_WIDTH,
                width: 1,
                height: "fit-content",
              }}
            >
              <Stack direction="row" gap={1}>
                <Typography id="plan-review-title" variant="h5">
                  Plan Review
                </Typography>
                {status && (
                  <Chip
                    variant="filled"
                    label={statusLabel(status)}
                    color={statusColor(status)}
                    data-testid="status-chip-label"
                  />
                )}
              </Stack>
              <DrawerControlButtons
                setDrawerOpen={setDrawerOpen}
                setSelectedDrawerPanel={setSelectedDrawerPanel}
              />
              <PhysicianReviewContent order={orderData} />
            </Stack>
          </Box>
          {orderData?.status === OrderStatusEnum.READY_FOR_RO_APPROVAL && (
            <Stack
              direction="row"
              gap={2}
              sx={{
                justifyContent: "flex-end",
                padding: 2,
                borderTop: `1px solid`,
                borderColor: "divider",
              }}
            >
              <Button
                variant="contained"
                color="error"
                onClick={() => setRejectPlanModalOpen(true)}
                size="large"
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={() => setApprovePlanModalOpen(true)}
              >
                Approve
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
      <ApprovePlanModal
        orderId={Number(orderId)}
        open={approvePlanModalOpen}
        onApprove={handleApprove}
        onClose={() => setApprovePlanModalOpen(false)}
      />
      <RejectPlanModal
        open={rejectPlanModalOpen}
        setOpen={setRejectPlanModalOpen}
        order={orderData}
        onReject={handleReject}
      />
      <OrderTrackerDrawer
        orderId={Number(orderId)}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        selectedPanel={selectedDrawerPanel}
        setSelectedPanel={setSelectedDrawerPanel}
        orderData={orderData}
        siteGroupData={siteGroupData || []}
      />
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default PhysicianReviewPage;
